import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import moment from 'moment';
import InfoPopover from '../../Common/InfoPopover';
import StatusBadge from '../../Common/StatusBadge';
import './employee-detail.css';

/**
 * Building blocks of the employee detail page. Every section on the page —
 * personal details, contract, compensation, assignment, bank, tax, vacation —
 * is a Section with Facts inside, so they read as one page rather than six
 * differently hand-rolled cards.
 */

export const formatDate = (date) => (date ? moment(date).format('D MMM YYYY') : '–');

export const formatPeriod = (row) =>
    `${formatDate(row.valid_from)} – ${row.valid_to ? formatDate(row.valid_to) : 'pågående'}`;

/**
 * Where a dated record sits relative to today. Used for the pill in every
 * history table; the same rule the page uses to pick the "current" record.
 */
export function periodStatus(row) {
    const today = moment().startOf('day');
    const from = moment(row.valid_from).startOf('day');
    const to = row.valid_to ? moment(row.valid_to).startOf('day') : null;
    if (from.isAfter(today, 'day')) return { tone: 'info', label: 'Kommande' };
    if (!to || to.isSameOrAfter(today, 'day')) return { tone: 'success', label: 'Aktuell' };
    return { tone: 'muted', label: 'Tidigare' };
}

export function Section({ id, title, hint, meta, badge, actions, children, className = '' }) {
    return (
        <section className={`emp-detail__section ${className}`.trim()} aria-labelledby={id}>
            <div className="emp-detail__head">
                <div className="emp-detail__title-wrap">
                    <h2 className="emp-detail__title" id={id}>{title}</h2>
                    {hint && <InfoPopover ariaLabel={`Om ${title}`}>{hint}</InfoPopover>}
                    {badge}
                    {meta && <p className="emp-detail__section-meta">{meta}</p>}
                </div>
                {actions && <div className="emp-detail__head-actions">{actions}</div>}
            </div>
            {children}
        </section>
    );
}

export function Facts({ children, tight = false }) {
    return <dl className={`emp-detail__facts${tight ? ' emp-detail__facts--tight' : ''}`}>{children}</dl>;
}

export function Fact({ label, hint, children, wide = false, tone }) {
    const empty = children === null || children === undefined || children === '' || children === '-';
    const classes = [
        'emp-detail__fact',
        wide && 'emp-detail__fact--wide',
        empty && 'emp-detail__fact--muted',
        tone && `emp-detail__fact--${tone}`,
    ].filter(Boolean).join(' ');
    return (
        <div className={classes}>
            <dt>
                {label}
                {hint && <InfoPopover ariaLabel={`Om ${label}`}>{hint}</InfoPopover>}
            </dt>
            <dd>{empty ? '–' : children}</dd>
        </div>
    );
}

export function UpcomingBadge({ show }) {
    return show ? <StatusBadge tone="info">Kommande</StatusBadge> : null;
}

export function HistoryToggle({ open, onToggle, count, noun = 'historik' }) {
    if (!count) return null;
    const Icon = open ? ChevronUp : ChevronDown;
    return (
        <button type="button" className="emp-detail__history-toggle" onClick={onToggle} aria-expanded={open}>
            <Icon size={14} />
            {open ? 'Dölj' : 'Visa'} {noun} ({count} tidigare)
        </button>
    );
}

export function HistoryTable({ columns, rows, rowKey, render }) {
    return (
        <div className="emp-detail__history">
            <table className="ll-table">
                <thead>
                    <tr>
                        {columns.map((c) => <th key={c}>{c}</th>)}
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const status = periodStatus(row);
                        return (
                            <tr key={row[rowKey]}>
                                {render(row).map((cell, i) => <td key={i}>{cell}</td>)}
                                <td><StatusBadge tone={status.tone}>{status.label}</StatusBadge></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export function SectionEmpty({ children, action }) {
    return (
        <div className="emp-detail__empty">
            <p>{children}</p>
            {action}
        </div>
    );
}
